/**
 * How a job is named to a hand.
 *
 * **The job number is the identity. The well name is context.**
 *
 * That is not a style preference — it is what a field worker is dispatched
 * against and what they are told on the phone. Derek Blattner's own words after
 * he could not clock in: *"it should show the job number to clock into."*
 *
 * ── WHY WELL NAME ALONE CANNOT IDENTIFY A JOB ───────────────────────────────
 *
 * It is not unique within a tenant, and the collisions are not rare. Measured
 * on production 2026-09-01, live jobs sharing a well name inside one company:
 *
 *   PAR   13-36-78-16W5                   4 jobs   J-0009, J-0013, J-0020, J-0032
 *   PAR   3-27-35-22W4-surf               4 jobs   J-0012, J-0016, J-0038, J-0044
 *   PAR   01-19-43-03W5                   3 jobs   J-0005, J-0015, J-0035
 *   TNT   CALTEX TRILOGY LIND 9-29-56-2   2 jobs   J-4298, J-4316
 *   TNT   CV 106                          2 jobs   J-4313, J-4314
 *   TNT   5-25                            2 jobs   J-4299, J-4308
 *   Dutch b-D59-I/94-B-16                 2 jobs   J-0002, J-0006
 *   Dutch HZ Two Rivers                   2 jobs   J-0016, J-0017
 *
 * Derek's clock-in picker showed him `CV 106`, `CV 106` and `CV106` — three
 * consecutive rows, two of them byte-identical and the third a missing space.
 * On PAR a hand faces FOUR indistinguishable rows.
 *
 * ── AND WHEN THERE IS NO WELL NAME, THE OLD CHAIN SAID NOTHING AT ALL ───────
 *
 * Three of ClockInScreen's four label sites read
 * `job?.name || job?.well_name || <placeholder>` — no term naming the job.
 * `jobs` HAS NO `name` COLUMN (verified against information_schema.columns,
 * 0 rows) and GET /jobs is `SELECT j.*`, so that first term has been undefined
 * since the screen shipped and the whole chain rested on `well_name`.
 *
 * J-4317's `well_name` is NULL. So Derek tapped, the sheet opened, he picked
 * J-4317, and the button fell through to the PLACEHOLDER — 'Select job', the
 * string that means *nothing is selected*. He picked again. He clocked in
 * anyway and the Current Assignment card read '—'. He clocked out after 12
 * seconds, retried, clocked out after 4. Both rows are in production carrying
 * `job_id` 720f449c: the selection always worked, only the label lied.
 *
 * 105 of 161 live jobs estate-wide have a NULL well_name — Dash 38/40, PAR
 * 35/49, Smith 24/24, TNT 7/30.
 *
 * ── THIS MATCHES WHAT THE REST OF THE FIELD APP ALREADY DOES ────────────────
 *
 * ClockInScreen was the only field screen labelling a job by its well name.
 * Every other one already leads with the number:
 *
 *   FieldJobsScreen          <h3>{job.job_number}</h3>
 *   FieldHomeScreen          <h4>{job.job_number}</h4>  (today and upcoming)
 *   FieldJobDetail           <h1>{job.job_number}</h1>, well name as a
 *                            secondary labelled row below
 *   FieldJobEquipmentScreen  {job.job_number}{well_name ? ` · ${well_name}` : ''}
 *   FieldEquipmentScreen     j.job_number || j.jobNumber || 'Job'
 *   FieldTicketsScreen       Job: {job_number …}
 *
 * The separator form below is FieldJobEquipmentScreen's, copied deliberately
 * rather than invented, so the two screens a hand moves between read the same.
 *
 * ── NUMBER FIRST ALSO SURVIVES TRUNCATION ──────────────────────────────────
 *
 * The trigger button is `truncate`d to one line. With the number leading, a
 * long well name loses its tail and the row stays identifiable; the other way
 * round, `CALTEX TRILOGY LIND 9-29-5…` clips exactly the part that told the two
 * CALTEX jobs apart.
 */

interface JobLike {
  /** Not a column on `jobs`. Kept for payloads that do carry one. */
  name?: string | null;
  well_name?: string | null;
  job_number?: string | null;
  id?: string | null;
}

/**
 * `J-4313 · CV 106`, or `J-4317` when the job has no well name.
 *
 * Returns null for NO JOB, and never a falsy string for a job in hand. Keeping
 * those two apart is the repair: *absent* and *nameless* were collapsing into
 * one string, and that string was the placeholder.
 *
 * `job.id` backs the number only if `job_number` is missing, which never
 * happens today — it is populated on all 161 live jobs — and is kept so the
 * label cannot bottom out. `name` is used as context only when there is no
 * `well_name`; it cannot arrive from GET /jobs at all, and is retained rather
 * than silently dropped.
 *
 * No trimming: 0 live jobs hold an empty or whitespace-only `well_name`, so a
 * `'   '` branch would be speculative. If one appears that is a new
 * measurement and a new line.
 */
export function jobLabel(job: JobLike | null | undefined): string | null {
  if (!job) return null;
  const number = job.job_number || job.id;
  const context = job.well_name || job.name;
  if (!number) return context || null;
  return context ? `${number} · ${context}` : String(number);
}

/** `jobLabel` with the caller's own word for "there isn't one". */
export function jobLabelOr(job: JobLike | null | undefined, fallback: string): string {
  return jobLabel(job) ?? fallback;
}
